"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
const NAV_ITEMS = [{
  label: "Build",
  href: "/build"
}, {
  label: "Service",
  href: "/service"
}, {
  label: "Repair",
  href: "/repair"
}, {
  label: "Inspections",
  href: "/inspections"
}, {
  label: "Renovations",
  href: "/renovations"
}, {
  label: "Legal",
  href: "/legal"
}];
const START_HERE_HREF = "/start-here";
const PORTAL_HREF = "https://rocketcitywaterwizards.mypoolportal.com";
const LOGO_SRC = "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/rcww-logo-2x-1.png";
function DesktopNav({
  scrolled
}: {
  scrolled: boolean;
}) {
  if (scrolled) {
    return <nav className="flex items-center gap-4 xl:gap-6 whitespace-nowrap shrink-0">
        {NAV_ITEMS.map(item => <Link key={item.href} to={item.href} className="text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest transition-colors whitespace-nowrap">
            {item.label}
          </Link>)}
      </nav>;
  }
  return <nav className="flex items-center gap-1 border border-border bg-accent/50 px-2 py-2 backdrop-blur-md whitespace-nowrap shrink-0">
      {NAV_ITEMS.map(item => <Link key={item.href} to={item.href} className="text-foreground hover:bg-accent font-mono text-sm uppercase tracking-widest px-3 xl:px-4 py-2 transition-colors whitespace-nowrap">
          {item.label}
        </Link>)}
    </nav>;
}
function MobileNav() {
  return <nav className="flex flex-col gap-2 px-6 mt-8">
      {NAV_ITEMS.map(item => <SheetClose key={item.href} asChild>
          <Link to={item.href} className="font-sans text-3xl text-foreground hover:text-primary transition-colors py-3 border-b border-border">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="719" id="command-center-header" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-lg border-b border-border" : "bg-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="h-20 md:h-24 flex items-center justify-between gap-4">
          {}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" aria-label="Rocket City Water Wizards home">
              <img src={LOGO_SRC} alt="Rocket City Water Wizards" className="h-14 md:h-[72px] w-auto block object-contain" style={{
              maxWidth: "none"
            }} />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center min-w-0">
            <DesktopNav scrolled={scrolled} />
          </div>

          {}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-sm transition-all shrink-0 whitespace-nowrap">
              <Link to={START_HERE_HREF}>
                Start Here
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/10 font-mono uppercase tracking-widest text-sm transition-all shrink-0 whitespace-nowrap">
              <Link to={PORTAL_HREF} newTab>Portal</Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden flex items-center gap-3 flex-shrink-0">
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider text-xs shrink-0">
              <Link to={START_HERE_HREF}>Start Here</Link>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-accent">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-md p-0">
                <SheetHeader className="px-6 pt-6 pb-2 flex flex-row items-center justify-between space-y-0">
                  <SheetTitle className="font-sans">
                    <img src={LOGO_SRC} alt="Rocket City Water Wizards" className="h-12 w-auto block object-contain" style={{
                    maxWidth: "none"
                  }} />
                  </SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-accent">
                      <X className="h-6 w-6" />
                    </Button>
                  </SheetClose>
                </SheetHeader>

                <MobileNav />

                <div className="px-6 mt-10 flex flex-col gap-3">
                  <SheetClose asChild>
                    <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-sm">
                      <Link to={START_HERE_HREF}>
                        Start Here
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button asChild variant="outline" size="lg" className="w-full border-primary text-primary hover:bg-primary/10 font-mono uppercase tracking-widest text-sm">
                      <Link to={PORTAL_HREF} newTab>Client Portal</Link>
                    </Button>
                  </SheetClose>
                </div>

                <div className="px-6 mt-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Huntsville, Alabama
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}